// input enums

export enum ProcessFrequency {
  DAILY = 260,
  WEEKLY = 52,
  BI_WEEKLY = 26,
  MONTHLY = 12,
  QUARTERLY = 4,
  ANNUALLY = 1
}
export enum InputDataStructure {
  NOT_DIGITAL_AND_UNSTRUCTURED = "notDigitalUnstructured",
  NOT_DIGITAL_AND_STRUCTURED = "notDigitalStructured",
  DIGITAL_AND_UNSTRUCTURED = "digitalUnstructured",
  DIGITAL_AND_STRUCTURED = "digitalStructured"
}

export enum AmountOfStructuredInputData {
  SMALLER_THAN_FORTY = 1,
  FORTY_TO_SIXTY = 0.7,
  SIXTY_TO_EIGHTY = 0.4,
  BIGGER_THAN_EIGHTY = 0
}
export enum ProcessStability {
  NO_CHANGE = 0,
  SMALL_CHANGE = 0.2,
  SOME_CHANGE = 0.4,
  MEDIUM_CHANGE = 0.8,
  SIGNIFICANT_CHANGE = 1
}
export enum ApplicationStability {
  NO_CHANGE = 0,
  SMALL_CHANGE = 0.2,
  SOME_CHANGE = 0.4,
  MEDIUM_CHANGE = 0.8,
  SIGNIFICANT_CHANGE = 1
}
export enum ProcessSteps {
  LESS_THAN_TEN = 0.1,
  TEN_TO_FIFTEEN = 0.2,
  FIFTEEN_TO_TWENTYFIVE = 0.4,
  TWENTYFIVE_TO_FOURTY = 0.6,
  MORE_THAN_FORTY = 1
}
export enum DecisionDifficulty {
  NO_DECISIONS = 0.1,
  SIMPLE_DECISIONS = 0.2,
  COMPLEX_DECISIONS = 0.7
}
export enum ApplicationsUsed {
  ONE = 0.1,
  TWO_TO_THREE = 0.3,
  FOUR_TO_FIVE = 0.6,
  MORE_THAN_FIVE = 1
}
export enum RemoteDesktopApplication {
  YES = 1.6,
  NO = 1
}
export enum InputScanned {
  YES = 1.2,
  NO = 1
}
export enum ProcessPeaks {
  NO_PEAKS = 0,
  REGULAR_PEAKS = 1,
  RARE_AND_PREDICTABLE_PEAKS = 2,
  RARE_AND_UNPREDICTABLE_PEAKS = 3
}

// output enums

export enum NoneFeasabilityResult {
  POSTPONE = "postpone",
  DIGITIZE_FIRST = "digitize_first",
  LOW_FEASIBILITY = "low_feasibility"
}

export enum EffortToImplementResult {
  LOW_EFFORT = 25,
  MEDIUM_EFFORT = 63,
  HIGH_EFFORT = 122
}

/**
 * Calculates how many Full-Time-Equivalents are needed to perform the process on a monthly basis.
 * @param processFrequency In which intervall is the process used ( Daily = 260, Weekly = 52, etc.).
 * @param frequencyVolume How often is it used in the given process frequency-period. (1 - n)
 * @param averageRuntime How much time does it take to run the complete process. Value in minutes. (1 - n)
 */
export function calculateFullTimeEquivalentNeededToPerform(
  processFrequency: ProcessFrequency,
  frequencyVolume: number,
  averageRuntime: number
): number {
  const FTE_FACTOR = 124800; // 6 * 80 * 260
  const fte =
    (processFrequency * frequencyVolume * averageRuntime) / FTE_FACTOR;
  return fte;
}

/**
 * Calculates the stability of the process over the next 6 months. (1 = many changes/low stability, 0 = no changes/high stability)
 * @param dataStructure Resembles wether the input data is structured or unstructured and if its digital or not.
 * @param processChanges Expresses the gravity of changes that will be made to the process in the next 6 months. (0 - 0.8)
 * @param applicationChanges Expresses the gravity of changes that will be made to the application in the next 6 months. (0 - 0.8)
 */
export function calculateOverallStability(
  dataStructure: InputDataStructure,
  processChanges: ProcessStability,
  applicationChanges: ApplicationStability
): number | NoneFeasabilityResult {
  if (dataStructure === InputDataStructure.NOT_DIGITAL_AND_STRUCTURED) {
    return NoneFeasabilityResult.DIGITIZE_FIRST;
  } else if (
    dataStructure === InputDataStructure.NOT_DIGITAL_AND_UNSTRUCTURED
  ) {
    return NoneFeasabilityResult.LOW_FEASIBILITY;
  } else if (
    processChanges === ProcessStability.SIGNIFICANT_CHANGE ||
    applicationChanges === ApplicationStability.SIGNIFICANT_CHANGE
  ) {
    return NoneFeasabilityResult.POSTPONE;
  } else {
    const STABILITY_FACTOR = 2;
    if (typeof processChanges === "string") {
      processChanges = parseInt((processChanges as unknown) as string);
    }
    if (typeof applicationChanges === "string") {
      applicationChanges = parseInt((applicationChanges as unknown) as string);
    }
    return (processChanges + applicationChanges) / STABILITY_FACTOR; // result wil be 0 - 1
  }
}
/**
 * Calculates the Ease Of Implementation of the automation of the process (100% --> easy to implement, 0% --> hard to implement).
 * @param overallProcessStability Resembles the amount of functional and structural changes in the process in the next 6 months. Can be calculated by calculateOverallStability.
 * @param applicationsUsedForProcess Number of additional applications used to finish the process. Values:  0.1, 0.3, 0.6, 1
 * @param processSteps Number of steps needed to finish the process. Values: 0.1, 0.2, 0.4, 0.6, 1
 * @param decisionDifficulty Resembles how difficult the decisions are you have to make. Values: 0.1, 0.2, 0.7
 * @param structuredInputData The amount of data that is structured. Values: 0, 0.4, 0.7, 1
 * @param remoteDesktopApplications Expresses wether remote applications are needed to perform the process. Values: 1, 1.6
 * @param inputScanned Expresses wether any of the input Data is scanned. Values: 1, 1.2
 */
export function calculateEaseOfImplementation(
  overallProcessStability: number | NoneFeasabilityResult,
  applicationsUsedForProcess: ApplicationsUsed,
  processSteps: ProcessSteps,
  decisionDifficulty: DecisionDifficulty,
  structuredInputData: AmountOfStructuredInputData,
  remoteDesktopApplications: RemoteDesktopApplication,
  inputScanned: InputScanned
): number | NoneFeasabilityResult {
  const MAX_EASE_OF_IMPLEMENTATION = 100;
  const STABILITY_FACTOR = 0.1;
  const APPLICATIONS_USED_FACTOR = 0.1;
  const PROCESS_STEPS_FACTOR = 0.25;
  const DECISION_DIFFICULTY_FACTOR = 0.25;
  const STRUCTURED_INPUT_FACTOR = 0.35;
  const DECIMAL_TO_PERCENTAGE_FACTOR = 100;

  if (typeof overallProcessStability != "number") {
    return overallProcessStability;
  }
  const weightedOverallProcessStability =
    overallProcessStability * STABILITY_FACTOR;
  const weightedApplicationsUsed =
    applicationsUsedForProcess * APPLICATIONS_USED_FACTOR;
  const weightedProcessSteps = processSteps * PROCESS_STEPS_FACTOR;
  const weightedDecisionDifficulty =
    decisionDifficulty * DECISION_DIFFICULTY_FACTOR;
  const weightedStructuredInputData =
    structuredInputData * STRUCTURED_INPUT_FACTOR;

  const easeOfImplementation =
    (weightedOverallProcessStability +
      weightedApplicationsUsed +
      weightedProcessSteps +
      weightedDecisionDifficulty +
      weightedStructuredInputData) *
    remoteDesktopApplications *
    inputScanned *
    DECIMAL_TO_PERCENTAGE_FACTOR;
  return Math.min(easeOfImplementation, MAX_EASE_OF_IMPLEMENTATION);
}

/**
 * Calculates a value that resembles wether or not a process is suited to be automated and the benfit gained with it (100%-> very suitable, high benefit | 0% --> not suited, no benefit).
 * @param digitalInputPercentage Percentage of the amount of data input that is digital. (0 - 100)
 * @param unfinishedProcessPercentage Percentage of processes that can only be finished by another person or not at all. (0 - 100)
 * @param structuredInputData The amount of data that is structured. Values: 0, 0.4, 0.7, 1.
 * @param decisionDifficulty Resembles how difficult the decisions are you have to make. Values: 0.1, 0.2, 0.7
 */
export function calculateBenefitSuitability(
  digitalInputPercentage: number,
  unfinishedProcessPercentage: number,
  structuredInputData: AmountOfStructuredInputData,
  decisionDifficulty: DecisionDifficulty
): number {
  const MINIMUM_BENEFIT_SUITABILITY = 0;
  const STRUCTURED_INPUT_FACTOR = 0.5;
  const DECISION_DIFFICULTY_FACTOR = 0.1;
  const UNFINISHED_PROCESS_TO_DECIMAL_FACTOR = 0.01;

  const weightedAmountOfDigitalInput =
    structuredInputData * STRUCTURED_INPUT_FACTOR;
  const weightedUnfinishedProcessPercentage =
    unfinishedProcessPercentage * UNFINISHED_PROCESS_TO_DECIMAL_FACTOR;
  const weightedDecisionDifficulty =
    decisionDifficulty * DECISION_DIFFICULTY_FACTOR;

  const calculatedBenefitSuitability =
    digitalInputPercentage *
    (1 -
      weightedUnfinishedProcessPercentage -
      weightedAmountOfDigitalInput -
      weightedDecisionDifficulty);

  return Math.max(calculatedBenefitSuitability, MINIMUM_BENEFIT_SUITABILITY);
}

/**
 * Calculates the amount of hours saved by automating the process.
 * @param processBenefitSuitability the benefit gained by the automation of the process. Is calculated by calculateBenefitSuitability. (0 - 100)
 * @param requiredFTEs Full-Time-Equivalent resembles how many employees are needed to finish the process at all the time. (0 - n)
 */
export function calculateBandwidthFreed(
  processBenefitSuitability: number,
  requiredFTEs: number
): number {
  const PERCENTAGE_TO_DECIMAL_FACTOR = 0.01;
  const BANDWIDTH_FACTOR = 2080;
  return (
    processBenefitSuitability *
    PERCENTAGE_TO_DECIMAL_FACTOR *
    requiredFTEs *
    BANDWIDTH_FACTOR
  );
}

/**
 * Calculates the amount by which errors will be reduced when the process is automated.
 * @param humanErrorPercentage Percentage of errors made by human workers in the process. (0 - 100)
 * @param digitalInputPercentage Percentage of the amount of data input that is digital. (0 - 100)
 * @param unfinishedProcessPercentage Percentage of processes that can only be finished by another person or not at all. (0 - 100)
 * @param inputScanned Value wether any of your input Data is scanned. Values: 1, 1.2
 * @param decisionDifficulty Resembles how difficult the decisions are you have to make. Values: 0.1, 0.2, 0.7
 */
export function calculateErrorReduction(
  humanErrorPercentage: number,
  digitalInputPercentage: number,
  unfinishedProcessPercentage: number,
  inputScanned: InputScanned,
  decisionDifficulty: DecisionDifficulty
): number {
  const HUMAN_ERROR_PERCENTAGE_MINIMUM = 0;
  if (humanErrorPercentage === HUMAN_ERROR_PERCENTAGE_MINIMUM) {
    return HUMAN_ERROR_PERCENTAGE_MINIMUM;
  }
  const DECIMAL_TO_PERCENTAGE_FACTOR = 100;
  const PERCENTAGE_TO_DECIMAL_FACTOR = 0.01;
  const INPUT_SCANNED_FACTOR = 0.5;
  const DECISION_DIFFICULTY_FACTOR = 0.1;

  const humanErrorDecimal = humanErrorPercentage * PERCENTAGE_TO_DECIMAL_FACTOR;
  const digitalInputDecimal =
    digitalInputPercentage * PERCENTAGE_TO_DECIMAL_FACTOR;
  const unfinishedProcessDecimal =
    unfinishedProcessPercentage * PERCENTAGE_TO_DECIMAL_FACTOR;
  const weightedInputScanned = inputScanned * INPUT_SCANNED_FACTOR;
  const weightedDecisionDifficulty =
    decisionDifficulty * DECISION_DIFFICULTY_FACTOR;

  const processDifficultySubCalculation =
    1 -
    unfinishedProcessDecimal -
    weightedInputScanned -
    weightedDecisionDifficulty;

  const subCalculation =
    digitalInputDecimal * processDifficultySubCalculation * humanErrorDecimal;

  const errorReduction =
    ((humanErrorDecimal - subCalculation) / humanErrorDecimal) *
    DECIMAL_TO_PERCENTAGE_FACTOR;
  return errorReduction;
}

/**
 * Calculates the amount by which the origial handling time is reduced when the process is automated.
 * @param processBenefitSuitability The benefit gained by the automation of the process.
 * Can be calculated with calculateBenefitSuitability. (0 - 100)
 */
export function calculateAverageHandlingTimeReduction(
  processBenefitSuitability: number
): number {
  const AVERAGE_HANDLING_TIME_REDUCTION_FACTOR = 2 / 3;
  return processBenefitSuitability * AVERAGE_HANDLING_TIME_REDUCTION_FACTOR;
}

/**
 * Calculates an amount of hours needed for the implementation of the process, depending on the ease of implementation.
 * @param easeOfImplementation Percentage of the ease of Implementation.
 * Can be calculated with calculateEaseOfImplementation. (0 - 100)
 */
export function calculateEffortToImplement(
  easeOfImplementation: number | NoneFeasabilityResult
): EffortToImplementResult | NoneFeasabilityResult {
  const MINIMUM_EFFORT = 10;
  const HIGH_EFFORT_CAP = 100;
  const EFFORT_DELTA = HIGH_EFFORT_CAP - MINIMUM_EFFORT;

  if (easeOfImplementation in NoneFeasabilityResult) {
    return easeOfImplementation;
  } else if (easeOfImplementation < HIGH_EFFORT_CAP) {
    const weightedEaseOfImplementation =
      (easeOfImplementation as number) * 0.75;
    return Math.ceil(
      MINIMUM_EFFORT + (EFFORT_DELTA * weightedEaseOfImplementation) / 100
    );
  } else {
    return easeOfImplementation;
  }
}
